import { Fragment } from 'react';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import { Box, Stack, Typography } from '@mui/material';
import type { Stage } from '../../shared/types';
import type { CandidateCheck } from '../../shared/candidateChecks';
import CandidateChecks from './CandidateChecks';

type PipelineSectionProps = {
  stage: Stage;
  rejectedFrom?: Stage;
  isRejected: boolean;
  checks: CandidateCheck[];
};

type PipelineStepId = Stage | 'Offer' | 'Hired';

type PipelineStep = {
  id: PipelineStepId;
  icon: React.ReactNode;
  color?: string;
  label?: string;
};

const PIPELINE_STEPS: PipelineStep[] = [
  { id: 'Applied', icon: <ArticleOutlinedIcon /> },
  { id: 'Evaluating', icon: <ChecklistOutlinedIcon /> },
  { id: 'Qualified', icon: <BalanceOutlinedIcon /> },
  { id: 'Offer', icon: <MarkEmailReadOutlinedIcon /> },
  { id: 'Hired', icon: <TrackChangesOutlinedIcon /> },
];

const REJECTED_VIEW = { icon: <CancelIcon />, color: 'error.main' };

const TERMINAL_STEPS: Partial<Record<Stage, PipelineStep>> = {
  Rejected: { id: 'Rejected', ...REJECTED_VIEW },
  Withdrawn: { id: 'Withdrawn', label: 'Withdrawn', ...REJECTED_VIEW },
  Closed: { id: 'Closed', label: 'Rejected', ...REJECTED_VIEW },
};

const getPipelineSteps = (stage: Stage, rejectedFrom: Stage | undefined, isRejected: boolean) => {
  const terminalStep = TERMINAL_STEPS[stage] ?? (isRejected ? TERMINAL_STEPS.Rejected : undefined);

  if (!terminalStep) {
    return PIPELINE_STEPS;
  }

  const stoppedAt = PIPELINE_STEPS.findIndex(step => step.id === (rejectedFrom ?? stage));

  return [...PIPELINE_STEPS.slice(0, stoppedAt + 1), terminalStep];
};

const getStepView = (step: PipelineStep, index: number, currentIndex: number) => {
  if (index < currentIndex) {
    return { icon: <CheckCircleIcon />, color: 'success.main' };
  }

  if (index > currentIndex) {
    return { icon: step.icon, color: 'text.secondary' };
  }

  return { icon: step.icon, color: step.color ?? 'success.main' };
};

const connectorSx = { flex: 1, minWidth: 5, maxWidth: 32, mx: 1, borderTop: 1 };

const PipelineSection = ({ stage, rejectedFrom, isRejected, checks }: PipelineSectionProps) => {
  const steps = getPipelineSteps(stage, rejectedFrom, isRejected);
  const currentIndex = steps.findIndex(step => step.id === stage);

  return (
    <Stack gap={2} sx={{ py: 1.5 }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" rowGap={1}>
        {steps.map((step, index) => {
          const { icon, color } = getStepView(step, index, currentIndex);

          return (
            <Fragment key={step.id}>
              {index > 0 && (
                <Box sx={{ ...connectorSx, borderColor: index <= currentIndex ? 'success.main' : 'divider' }} />
              )}
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ color, '& svg': { fontSize: 18 } }}>
                {icon}
                <Typography variant="body1" color="inherit" fontWeight={index === currentIndex ? 600 : 400} noWrap>
                  {step.label ?? step.id}
                </Typography>
              </Stack>
            </Fragment>
          );
        })}
      </Stack>

      {checks.length > 0 && <CandidateChecks checks={checks} />}
    </Stack>
  );
};

export default PipelineSection;
